import React from 'react';
import { BookOpen, Shield, Users, FileText, Settings, HelpCircle } from 'lucide-react';

interface ResourceCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  link: string;
}

const ResourceCard = ({ icon: Icon, title, description, link }: ResourceCardProps) => (
  <div className="group bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
    <Icon className="h-12 w-12 text-[#5e9bb8] dark:text-[#5e9bb8] mb-6 transform group-hover:scale-110 transition-transform duration-300" />
    <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white group-hover:text-[#5e9bb8] dark:group-hover:text-[#5e9bb8] transition-colors duration-300">{title}</h3>
    <p className="text-gray-600 dark:text-gray-300 mb-4">{description}</p>
    <a 
      href={link} 
      className="inline-flex items-center text-[#5e9bb8] hover:text-[#499da8] font-medium transition-colors duration-300"
      target="_blank"
      rel="noopener noreferrer"
    >
      Learn More →
    </a>
  </div>
);

export function Resources() {
  const resources = [
    {
      icon: BookOpen,
      title: "Documentation",
      description: "Comprehensive guides and documentation for OneTrust implementation and configuration.",
      link: "https://my.onetrust.com/s/article/UUID-d246f4bc-4537-5f56-1aa2-89aa40ca9c67"
    },
    {
      icon: Shield,
      title: "Privacy Management",
      description: "Best practices and guidelines for managing privacy compliance with OneTrust.",
      link: "https://my.onetrust.com/s/article/UUID-f69d7f6a-c33b-8b99-3368-1c7aa6622200"
    },
    {
      icon: Users,
      title: "Consent Management",
      description: "Learn how to effectively manage user consent and preferences across your digital properties.",
      link: "https://my.onetrust.com/s/article/UUID-0b831e49-c6aa-2aed-c51d-89b0462dfe79"
    },
    {
      icon: FileText,
      title: "Templates & Forms",
      description: "Access ready-to-use templates and forms for privacy notices, consent banners, and more.",
      link: "https://my.onetrust.com/s/article/UUID-d8743f2f-b06d-7df7-6ffd-1808cf726c63"
    },
    {
      icon: Settings,
      title: "Technical Guides",
      description: "Detailed technical documentation for developers implementing OneTrust solutions.",
      link: "https://my.onetrust.com/s/article/UUID-6c6b5c1d-4c8c-8b99-7a68-1c7aa6622200"
    },
    {
      icon: HelpCircle,
      title: "Support Resources",
      description: "Access support articles, FAQs, and troubleshooting guides for common issues.",
      link: "https://my.onetrust.com/s/article/UUID-f69d7f6a-c33b-8b99-3368-1c7aa6622200"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8] mb-4">
            OneTrust Resources
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Everything you need to succeed with OneTrust implementation and management
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {resources.map((resource, index) => (
            <ResourceCard
              key={index}
              icon={resource.icon}
              title={resource.title}
              description={resource.description}
              link={resource.link}
            />
          ))}
        </div>

        <div className="mt-16 bg-[#5e9bb8]/5 dark:bg-[#5e9bb8]/10 backdrop-blur-sm rounded-2xl p-8 text-center transform hover:-translate-y-1 transition-all duration-300">
          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8] mb-4">
            Need Additional Help?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Our team of OneTrust experts is here to help you succeed.
          </p>
          <a
            href="https://my.onetrust.com/s/contactsupport"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex px-6 py-3 bg-gradient-to-r from-[#5e9bb8] to-[#499da8] hover:from-[#4d8299] hover:to-[#3d8289] text-white rounded-xl font-medium transform hover:-translate-y-0.5 transition-all duration-300"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}